import { CommonModule } from '@angular/common';
import { Component, inject, input, model, output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { Popover, PopoverModule } from 'primeng/popover';
import { Column } from 'src/app/models/table';
import { AppButton } from '../button/app-button';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { AccountService } from 'src/app/services/account-settings/account-service';
import { ToastService } from 'src/app/services/toast/toast-service';

@Component({
  selector: 'app-table-customize-popup',
  imports: [CommonModule, PopoverModule, CheckboxModule, FormsModule, AppButton, TranslationPipe],
  templateUrl: './table-customize-popup.html',
  styleUrl: './table-customize-popup.scss'
})
export class TableCustomizePopup {
  private accountService = inject(AccountService);
  private toastService = inject(ToastService);

  columns = model<Column[]>([]);
  moduleCode = input<string>('');
  doneLoading = output<boolean>();

  visibleColumns: Column[] = [];

  isSaved: boolean = false;
  disableUpdateBtn: boolean = true;

  @ViewChild('customizePopover') customizePopover!: Popover;

  ngOnInit() {
    this.setColumns();
  }

  setColumns() {
    this.doneLoading.emit(false);

    this.columns.set([...this.columns()]);
    this.visibleColumns = this.columns().filter((col) => !col.hide);

    this.accountService.getCustomizationColumn(this.moduleCode()).subscribe({
      next: (response: any) => {
        const customizeHiddenColumns = response.data.propertyNames;

        this.visibleColumns = this.visibleColumns.map((col) => ({
          ...col,
          customizeHide: customizeHiddenColumns?.includes(col.id)
        }));

        this.columns.set(this.visibleColumns);

        this.doneLoading.emit(true);
      }
    });
  }

  onCheckboxChange(event: any, column: Column) {
    column.customizeHide = !event;
    const currentHiddenIds = this.visibleColumns
      .filter((c) => c.customizeHide)
      .map((c) => c.id)
      .sort();
    const same = JSON.stringify(currentHiddenIds) === JSON.stringify(this.visibleColumns.map((c) => c.customizeHide));
    this.disableUpdateBtn = same;

    this.columns.set([...this.columns()]);
  }

  onUpdate(showToast: boolean = true) {
    const propertyNames = this.visibleColumns.filter((col) => col.customizeHide === true).map((col) => col.id);
    const data = {
      moduleCode: this.moduleCode(),
      propertyNames: propertyNames
    };

    this.accountService.updateCustomizationColumn(data).subscribe({
      next: (response: any) => {
        this.isSaved = true;
        this.setColumns();

        if (showToast) this.toastService.success(response.message);
      }
    });

    this.customizePopover.hide();
  }

  onClickOutReset() {
    const hasChanges = this.checkChanges();

    if (!this.isSaved && hasChanges) {
      this.columns.set(structuredClone(this.visibleColumns));
      this.setColumns();
    }
  }

  checkChanges() {
    const currentHiddenIds = this.visibleColumns
      .filter((c) => c.customizeHide)
      .map((c) => c.id)
      .sort();

    return !(JSON.stringify(currentHiddenIds) === JSON.stringify(this.visibleColumns.map((c) => c.customizeHide)));
  }

  onReset() {
    this.visibleColumns = this.visibleColumns.map((col) => ({
      ...col,
      customizeHide: false
    }));

    this.columns.set(this.visibleColumns);
    this.onUpdate(false);
  }

  toggle(event: Event) {
    this.customizePopover.toggle(event);
  }

  onShow() {
    this.isSaved = false;
    this.disableUpdateBtn = true;
  }

  buildHeaderParams(col: Column) {
    if (col.customizeHide) {
      this.disableUpdateBtn = false;
    }
    if (!col.headerTranslationParams || col.headerTranslationParams.length === 0) {
      return {}; // no params → translator works normally
    }

    // Convert ["MYR", "year"] to {0: "MYR", 1: "year"}
    return col.headerTranslationParams.reduce((acc, val, index) => {
      acc[index] = val;
      return acc;
    }, {} as any);
  }
}
